import React from 'react';

export interface StatsEmptyStateLabels {
  title: string;
  description: string;
  moodPatterns: string;
  moodPatternsHint: string;
  weeklyTrends: string;
  weeklyTrendsHint: string;
  activityImpact: string;
  activityImpactHint: string;
  moodByWeekday: string;
  mostCommonMood: string;
  unlockBadge: string;
  ctaButton: string;
  daysHint: string;
}

export interface StatsEmptyStateProps {
  diaryUrl: string;
  labels: StatsEmptyStateLabels;
}

export function StatsEmptyState({ diaryUrl, labels }: StatsEmptyStateProps) {
  return (
    <div className="insights-preview mt-4">
      <div className="card card-content">
        <div className="card-content-inner text-center py-5">
          <div
            className="icon-wrapper icon-wrapper-lg mx-auto mb-4"
            style={{ background: 'var(--traq-gradient-primary)' }}
          >
            <i className="bi bi-graph-up-arrow text-white"></i>
          </div>

          <h2 className="heading-primary mb-3">{labels.title}</h2>
          <p className="text-muted mb-4 mx-auto" style={{ maxWidth: 500 }}>
            {labels.description}
          </p>

          {/* Sample Charts Preview */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div className="preview-card p-3 rounded-3 bg-traq-light">
                <i className="bi bi-emoji-smile fs-2 text-primary mb-2 d-block"></i>
                <h6 className="fw-semibold mb-1">{labels.moodPatterns}</h6>
                <small className="text-muted">{labels.moodPatternsHint}</small>
              </div>
            </div>
            <div className="col-md-4">
              <div className="preview-card p-3 rounded-3 bg-traq-light">
                <i className="bi bi-calendar-week fs-2 text-primary mb-2 d-block"></i>
                <h6 className="fw-semibold mb-1">{labels.weeklyTrends}</h6>
                <small className="text-muted">{labels.weeklyTrendsHint}</small>
              </div>
            </div>
            <div className="col-md-4">
              <div className="preview-card p-3 rounded-3 bg-traq-light">
                <i className="bi bi-lightning-charge fs-2 text-primary mb-2 d-block"></i>
                <h6 className="fw-semibold mb-1">{labels.activityImpact}</h6>
                <small className="text-muted">{labels.activityImpactHint}</small>
              </div>
            </div>
          </div>

          {/* Sample Chart Image (blurred placeholder) */}
          <div
            className="sample-chart-preview mb-4 p-4 rounded-3 position-relative"
            style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              overflow: 'hidden',
            }}
          >
            <div className="row">
              <div className="col-6">
                <div
                  className="d-flex align-items-end justify-content-around"
                  style={{ height: 120 }}
                >
                  <div className="bg-primary rounded" style={{ width: 20, height: '40%', opacity: 0.3 }}></div>
                  <div className="bg-primary rounded" style={{ width: 20, height: '60%', opacity: 0.4 }}></div>
                  <div className="bg-primary rounded" style={{ width: 20, height: '80%', opacity: 0.5 }}></div>
                  <div className="bg-primary rounded" style={{ width: 20, height: '55%', opacity: 0.6 }}></div>
                  <div className="bg-primary rounded" style={{ width: 20, height: '70%', opacity: 0.7 }}></div>
                  <div className="bg-primary rounded" style={{ width: 20, height: '90%', opacity: 0.8 }}></div>
                  <div className="bg-primary rounded" style={{ width: 20, height: '65%', opacity: 0.9 }}></div>
                </div>
                <small className="text-muted d-block mt-2">{labels.moodByWeekday}</small>
              </div>
              <div className="col-6">
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ height: 120 }}
                >
                  <svg viewBox="0 0 100 100" style={{ width: 100, height: 100, transform: 'rotate(-90deg)' }}>
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#e9ecef" strokeWidth="20" />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#667eea"
                      strokeWidth="20"
                      strokeDasharray="125.6 125.6"
                      opacity="0.5"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#764ba2"
                      strokeWidth="20"
                      strokeDasharray="75.4 175.8"
                      strokeDashoffset="-125.6"
                      opacity="0.6"
                    />
                  </svg>
                </div>
                <small className="text-muted d-block mt-2">{labels.mostCommonMood}</small>
              </div>
            </div>
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(2px)' }}
            >
              <span className="badge bg-primary px-3 py-2">{labels.unlockBadge}</span>
            </div>
          </div>

          <div className="d-flex flex-column align-items-center gap-2">
            <a href={diaryUrl} className="btn-primary-gradient btn-lg">
              <i className="bi bi-journal-plus me-2"></i>
              {labels.ctaButton}
            </a>
            <small className="text-muted">{labels.daysHint}</small>
          </div>
        </div>
      </div>

      <style>{`
        .insights-preview .preview-card {
          transition: transform 0.2s ease;
        }
        .insights-preview .preview-card:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
}

export default StatsEmptyState;
